import { FormEvent, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Camera,
  CircleDollarSign,
  Mail,
  Plus,
  SearchCheck,
  Warehouse,
} from 'lucide-react';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { blogPosts } from '../content/blogPosts';
import { submitWaitlist } from '../services/waitlistService';
import { cn } from '../utils/cn';
import { useSeo } from '../utils/useSeo';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const heroSectionImage = '/car-dealer-hero.png';
const dealsFinderImage = '/deals-finder.png';
const financeLaptopImage = '/finance-laptop.png';
const aiBeforeImage = '/before-optimized.jpeg';
const aiAfterImage = '/after.jpeg';
const heroToolPills = ['Deals Finder', 'AI Photo', 'Garage', 'Finance'] as const;
const heroPillPositions: Record<(typeof heroToolPills)[number], string> = {
  'Deals Finder': 'left-0 top-[34%] -translate-x-[24%] -translate-y-1/2 sm:-translate-x-[30%]',
  'AI Photo': 'left-0 top-[62%] -translate-x-[24%] -translate-y-1/2 sm:-translate-x-[30%]',
  Garage: 'right-0 top-[44%] translate-x-[24%] -translate-y-1/2 sm:translate-x-[30%]',
  Finance: 'right-0 top-[70%] translate-x-[24%] -translate-y-1/2 sm:translate-x-[30%]',
};
const dealsFinderBenefits = [
  'Spot underpriced cars before your competition.',
  'Cut manual sourcing time and focus only on profitable deals.',
  'Move faster on buys and improve margin consistency.',
  'Turn sourcing into a repeatable system instead of guesswork.',
] as const;
const aiPhotographyBenefits = [
  'Cleaner, brighter car photos in one click.',
  'Consistent look across every listing.',
  'Better photos help justify higher asking prices.',
] as const;
const financeManagementBenefits = [
  'Track purchase price, repairs, fees, and final profit automatically',
  'See real margins per car — not rough estimates',
  'Export clean reports for accounting or partners in seconds',
  'Keep every deal documented and easy to review later',
] as const;
const benefitListVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.14,
    },
  },
};
const benefitItemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.34 },
  },
};
const tickIconVariants = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.28 },
  },
};
const tickPathVariants = {
  hidden: { opacity: 0, pathLength: 0 },
  visible: {
    opacity: 1,
    pathLength: 1,
    transition: { duration: 0.38, delay: 0.06 },
  },
};

function AnimatedTick() {
  return (
    <motion.svg
      aria-hidden="true"
      className="mt-0.5 h-6 w-6 shrink-0"
      variants={tickIconVariants}
      viewBox="0 0 24 24"
    >
      <motion.path
        d="M5.5 12.5l4 4 9-10"
        fill="none"
        stroke="#53b12c"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.6"
        variants={tickPathVariants}
      />
    </motion.svg>
  );
}

export function LandingPageV2() {
  useSeo({
    description:
      'Archived version of the Profit Cars landing page.',
    image: '/car-dealer-hero.png',
    keywords: ['profit cars', 'landing page archive'],
    noindex: true,
    path: '/v2',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      description: 'Archived version of the Profit Cars landing page.',
      name: 'Profit Cars v2',
      url: '/v2',
    },
    title: 'Profit Cars v2 | Archive',
    type: 'website',
  });

  const navigate = useNavigate();
  const [optinEmail, setOptinEmail] = useState('');
  const [optinError, setOptinError] = useState<string | null>(null);
  const [optinConsent, setOptinConsent] = useState(false);
  const [optinSubmitting, setOptinSubmitting] = useState(false);

  const handleHeroEarlyAccessClick = () => {
    document.getElementById('optin-get')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleOptinSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (optinSubmitting) {
      return;
    }

    const trimmedEmail = optinEmail.trim();

    if (!trimmedEmail) {
      setOptinError('Email is required.');
      return;
    }

    if (!emailPattern.test(trimmedEmail)) {
      setOptinError('Please enter a valid email address.');
      return;
    }

    if (!optinConsent) {
      setOptinError('Please confirm consent before continuing.');
      return;
    }

    setOptinError(null);
    setOptinSubmitting(true);

    try {
      await submitWaitlist({
        email: trimmedEmail,
        source: 'hero',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to join waitlist';
      setOptinError(message);
      setOptinSubmitting(false);
      return;
    }

    setOptinSubmitting(false);
    navigate(`/get?email=${encodeURIComponent(trimmedEmail)}`);
  };

  return (
    <main className="overflow-x-hidden bg-[#ececec] text-[#111827]">
      <section className="relative border-b border-black/10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url('/europe-map.svg')",
            backgroundPosition: '62% 38%',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '248%',
            filter: 'grayscale(1) brightness(0.42) contrast(0.95)',
            opacity: 0.24,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/90 via-[#efefef]/90 to-[#e3e3e3]/88" />

        <div className="relative mx-auto max-w-6xl px-6 pb-16 pt-10 sm:pb-20 lg:px-8">
          <div className="flex flex-col items-center gap-5">
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="w-full max-w-2xl text-center"
              initial={{ opacity: 0, y: 16 }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-[#53b12c]/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-[#2f5b1b]">
                Made for EU Car dealers.
                <span aria-hidden="true" className="text-base leading-none">
                  🇪🇺
                </span>
              </div>

              <h1 className="font-display mt-5 text-4xl font-normal leading-[1.05] text-[#101010] sm:text-5xl lg:text-6xl">
                The all-in-one operating system for car dealers
              </h1>

              <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-700">
                Built for EU dealers who want faster sourcing, cleaner listings, and clear margin visibility on every
                car they flip or sell.
              </p>
            </motion.div>

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="w-full"
              initial={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.05 }}
            >
              <div className="relative mx-auto w-full max-w-[620px] pt-5 md:pt-8">
                <div className="relative mx-auto w-full max-w-[520px]">
                  <img
                    alt="Car dealer workflow preview"
                    className="aspect-[16/9] w-full object-contain mix-blend-multiply"
                    src={heroSectionImage}
                  />

                  <div className="pointer-events-none">
                    {heroToolPills.map((pill) => (
                      <span
                        key={pill}
                        className={cn(
                          'absolute inline-flex items-center gap-1.5 rounded-full border border-black/12 bg-white/80 px-3 py-1.5 text-center text-[11px] font-semibold text-[#111827] sm:gap-2 sm:px-4 sm:py-2 sm:text-sm',
                          heroPillPositions[pill]
                        )}
                      >
                        {pill === 'Deals Finder' && <SearchCheck className="h-3.5 w-3.5" />}
                        {pill === 'AI Photo' && <Camera className="h-3.5 w-3.5" />}
                        {pill === 'Garage' && <Warehouse className="h-3.5 w-3.5" />}
                        {pill === 'Finance' && <CircleDollarSign className="h-3.5 w-3.5" />}
                        {pill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 26 }}
              transition={{ duration: 0.55, ease: 'easeOut', delay: 0.12 }}
            >
              <Button
                className="relative isolate mt-2 h-14 min-w-[280px] overflow-hidden rounded-[10px] border border-black/65 bg-[#08090c] px-9 font-bold tracking-[0.06em] text-white shadow-none hover:border-white/35 hover:bg-[#141820] focus-visible:outline-[#08090c] after:pointer-events-none after:absolute after:inset-y-0 after:-left-[55%] after:w-[42%] after:skew-x-[-22deg] after:bg-gradient-to-r after:from-transparent after:via-white/55 after:to-transparent after:opacity-0 after:transition-all after:duration-700 hover:after:left-[130%] hover:after:opacity-100"
                onClick={handleHeroEarlyAccessClick}
                type="button"
              >
                GET ACCESS TO PROFIT CARS APP
                <ArrowRight className="h-4 w-4" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-[#f6f6f6] pb-24 pt-24 sm:pb-28 sm:pt-28" id="tools">
        <div className="mx-auto w-full max-w-5xl px-6 text-center lg:px-8">
          <h2 className="font-display text-4xl font-normal leading-[1.05] text-[#101010] sm:text-5xl lg:text-6xl">
            🛠️ Tools that give you <span className="font-bold">Your Unfair Advantage</span> in the car dealer
            industry in 2026. 💸
          </h2>
        </div>
      </section>

      <section className="bg-[#f6f6f6] pb-20 pt-6 sm:pb-24 sm:pt-10">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <div className="space-y-14">
            <article className="grid gap-8 md:grid-cols-[1fr_1fr] md:gap-10">
              <div>
                <h3 className="font-display text-4xl leading-tight text-black sm:text-5xl">
                  AI PHOTOGRAPHY THAT MAKES EVERY LISTING STAND OUT.
                </h3>
                <p className="mt-5 text-base leading-relaxed text-slate-700">Images that make your ad/listing stand out.</p>

                <motion.ul
                  className="mt-5 space-y-4 text-base leading-relaxed text-slate-700"
                  initial="hidden"
                  variants={benefitListVariants}
                  viewport={{ amount: 0.35, once: true }}
                  whileInView="visible"
                >
                  {aiPhotographyBenefits.map((benefit) => (
                    <motion.li key={benefit} className="flex items-start gap-3" variants={benefitItemVariants}>
                      <AnimatedTick />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </div>

              <div className="rounded-[24px] border border-black/10 bg-[#f3f4f6] p-4">
                <div className="relative aspect-[75/56] overflow-hidden rounded-[18px] border border-black/15 bg-black">
                  <ReactCompareSlider
                    className="h-full w-full"
                    defaultPosition={56}
                    onlyHandleDraggable={false}
                    transition="0.24s cubic-bezier(0.22,1,0.36,1)"
                    itemOne={
                      <ReactCompareSliderImage
                        alt="Before listing visual"
                        src={aiBeforeImage}
                        style={{ filter: 'brightness(0.78) saturate(0.65)', height: '100%', objectFit: 'cover', width: '100%' }}
                      />
                    }
                    itemTwo={
                      <ReactCompareSliderImage
                        alt="After listing visual"
                        src={aiAfterImage}
                        style={{ height: '100%', objectFit: 'cover', width: '100%' }}
                      />
                    }
                  />

                  <div className="pointer-events-none absolute left-4 top-4 z-20 whitespace-nowrap rounded-full bg-black/70 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-white">
                    Before
                  </div>
                  <div className="pointer-events-none absolute right-4 top-4 z-20 whitespace-nowrap rounded-full bg-[#53b12c]/90 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-black">
                    After
                  </div>
                </div>
              </div>
            </article>

            <div className="relative flex items-center">
              <div className="h-px w-full bg-black/15" />
              <span className="absolute left-1/2 inline-flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border border-black/20 bg-white text-slate-700">
                <Plus className="h-4 w-4" />
              </span>
            </div>

            <article className="grid gap-8 md:grid-cols-[0.92fr_1.08fr] md:gap-10">
              <div>
                <h3 className="font-display text-4xl leading-tight text-black sm:text-5xl">DEALS FINDER</h3>
                <motion.ul
                  className="mt-6 space-y-5 text-base leading-relaxed text-slate-700"
                  initial="hidden"
                  variants={benefitListVariants}
                  viewport={{ amount: 0.35, once: true }}
                  whileInView="visible"
                >
                  {dealsFinderBenefits.map((benefit) => (
                    <motion.li key={benefit} className="flex items-start gap-3" variants={benefitItemVariants}>
                      <AnimatedTick />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </div>

              <div className="flex items-center">
                <img
                  alt="Deals Finder interface preview"
                  className="h-auto w-full"
                  loading="lazy"
                  src={dealsFinderImage}
                />
              </div>
            </article>

            <div className="relative flex items-center">
              <div className="h-px w-full bg-black/15" />
              <span className="absolute left-1/2 inline-flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border border-black/20 bg-white text-slate-700">
                <Plus className="h-4 w-4" />
              </span>
            </div>

            <article className="grid gap-8 md:grid-cols-[1fr_1fr] md:gap-10">
              <div>
                <h3 className="font-display text-4xl leading-tight text-black sm:text-5xl">
                  GARAGE AND FINANCE MANAGEMENT.
                </h3>
                <p className="mt-5 text-base leading-relaxed text-slate-700">
                  Know your exact profit on every car — before and after the sale.
                </p>
                <motion.ul
                  className="mt-6 space-y-4 text-base leading-relaxed text-slate-700"
                  initial="hidden"
                  variants={benefitListVariants}
                  viewport={{ amount: 0.35, once: true }}
                  whileInView="visible"
                >
                  {financeManagementBenefits.map((benefit) => (
                    <motion.li key={benefit} className="flex items-start gap-3" variants={benefitItemVariants}>
                      <AnimatedTick />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </div>

              <div className="flex min-h-[280px] items-center justify-end">
                <img
                  alt="Finance workflow on laptop"
                  className="h-auto w-full max-w-[560px] object-contain"
                  loading="lazy"
                  src={financeLaptopImage}
                />
              </div>
            </article>
          </div>

        </div>
      </section>

      <section className="py-24 sm:py-28" id="optin-get">
        <div className="mx-auto w-full max-w-4xl px-6 lg:px-8">
          <h2 className="mx-auto max-w-4xl text-center font-display text-5xl leading-[0.95] text-black sm:text-6xl lg:text-7xl">
            <span className="text-[#9ea2a8]">Get Access to</span>{' '}
            <span className="text-black">Profit Cars.</span>
          </h2>

          <form className="mx-auto mt-10 max-w-3xl" onSubmit={handleOptinSubmit}>
            <label className="sr-only" htmlFor="optin-email">
              Business email
            </label>
            <div className="mx-auto flex h-[66px] w-full max-w-[390px] items-center gap-3 rounded-[16px] border border-black/15 bg-white px-4 sm:px-5">
              <Mail className="h-6 w-6 shrink-0 text-black/65 sm:h-6 sm:w-6" strokeWidth={1.85} />
              <input
                className="h-full w-full bg-transparent text-base font-semibold text-black outline-none placeholder:text-slate-400 sm:text-lg"
                disabled={optinSubmitting}
                id="optin-email"
                onChange={(event) => {
                  setOptinEmail(event.target.value);
                  if (optinError) {
                    setOptinError(null);
                  }
                }}
                placeholder="Enter your email"
                type="email"
                value={optinEmail}
              />
            </div>

            <label className="mx-auto mt-6 flex w-full max-w-[390px] items-start gap-2.5 text-left text-sm leading-relaxed text-slate-500">
              <input
                checked={optinConsent}
                className="mt-1 h-4 w-4 rounded border border-black/35 accent-[#48a7e6]"
                disabled={optinSubmitting}
                onChange={(event) => {
                  setOptinConsent(event.target.checked);
                  if (optinError) {
                    setOptinError(null);
                  }
                }}
                type="checkbox"
              />
              <span>
                I agree to receiving email updates from Profit Cars and confirm that I read and agree with{' '}
                <span className="underline">Terms of Use</span> and <span className="underline">Privacy Policy</span>.
              </span>
            </label>

            {optinError && (
              <p
                className="mx-auto mt-4 w-full max-w-[390px] rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                role="alert"
              >
                {optinError}
              </p>
            )}

            <div className="mt-9 flex justify-center">
              <Button
                className="relative isolate flex h-14 w-full max-w-[390px] overflow-hidden rounded-[10px] border border-black/65 bg-[#08090c] px-7 text-lg font-bold tracking-[0.04em] text-white shadow-none hover:border-white/35 hover:bg-[#141820] focus-visible:outline-[#08090c] after:pointer-events-none after:absolute after:inset-y-0 after:-left-[55%] after:w-[42%] after:skew-x-[-22deg] after:bg-gradient-to-r after:from-transparent after:via-white/55 after:to-transparent after:opacity-0 after:transition-all after:duration-700 hover:after:left-[130%] hover:after:opacity-100"
                disabled={optinSubmitting}
                type="submit"
              >
                {optinSubmitting ? 'Submitting...' : 'Go to Step #2'}
              </Button>
            </div>
          </form>
        </div>
      </section>

      <section className="bg-[#f6f6f6] py-20 sm:py-24" id="insights">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mt-4 font-display text-5xl leading-[0.95] text-black sm:text-6xl">
              insights every car dealer should know
            </h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {blogPosts.map((post, index) => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                key={post.slug}
                transition={{ delay: index * 0.08, duration: 0.4, ease: 'easeOut' }}
                viewport={{ amount: 0.2, once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <Link
                  className="group block overflow-hidden rounded-[24px] border border-black/10 bg-white transition hover:border-black/25 hover:bg-white"
                  to={`/blog/${post.slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-black/5">
                    <img
                      alt={post.title}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                      loading="lazy"
                      src={post.image}
                    />
                  </div>

                  <div className="p-5">
                    <h3 className="text-xl font-extrabold leading-tight text-black">{post.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-700">{post.description}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.09em] text-[#2f5b1b]">
                      Read article
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Link
              className="inline-flex h-12 items-center justify-center rounded-[10px] border border-black/65 bg-[#08090c] px-8 text-sm font-bold uppercase tracking-[0.06em] text-white transition hover:border-white/35 hover:bg-[#141820]"
              to="/blog"
            >
              See all blog posts
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
